// src/helpers/databaseMapping.js
import { getActiveProviderSlug } from "./provider";

export const DB_STORAGE_KEY = "theearcasino-frontend-fb";
export const DEFAULT_DATABASE = "theearcasino";

export const DB_MAPPING = {
  DEFAULT: "theearcasino",
  THEEARCASINO: "theearcasino",
  LIVECASINO: "livecasino",
  ROULETTE: "livecasino",
};

/**
 * Returns the currently active category/section ('slot', 'livecasino', 'instant', etc.)
 * @returns {string}
 */
export const getActiveCategory = () => {
  try {
    if (typeof window !== "undefined" && window.location) {
      const path = window.location.pathname || "";
      if (path.includes("/livecasino")) return "livecasino";
      if (path.includes("/instant")) return "instant";
      if (path.includes("/slot")) return "slot";
    }
  } catch (error) {
    console.error("Error getting active category:", error);
  }
  return "slot";
};

/**
 * Computes the target database name by concatenating the base provider / db with '_${categoriaSelezionata}'
 * @param {string|boolean|null} targetDb - Optional explicit database, provider, or category name
 * @returns {string} e.g. "theearcasino_slot", "theearcasino_livecasino", "studiocasinolab_instant"
 */
export const getComputedDatabase = (targetDb = null) => {
  const provider = getActiveProviderSlug() || DEFAULT_DATABASE;
  const category = getActiveCategory() || "slot";

  // If no targetDb or false/null/undefined passed, use active provider + category
  if (!targetDb || targetDb === true) {
    return `${provider}_${category}`;
  }

  if (typeof targetDb === "string") {
    const trimmed = targetDb.trim();
    if (!trimmed) {
      return `${provider}_${category}`;
    }

    // If it already has an underscore and includes a section (e.g. 'theearcasino_slot')
    if (trimmed.includes("_")) {
      return trimmed;
    }

    // If targetDb is a section/category name (e.g. 'slot', 'livecasino', 'instant')
    if (
      ["slot", "livecasino", "instant", "roulette"].includes(
        trimmed.toLowerCase()
      )
    ) {
      const mappedCategory =
        trimmed.toLowerCase() === "roulette"
          ? "livecasino"
          : trimmed.toLowerCase();
      return `${provider}_${mappedCategory}`;
    }

    // If targetDb is a provider slug (e.g. 'theearcasino', 'studiocasinolab')
    if (trimmed === "theearcasino" || trimmed === "studiocasinolab") {
      return `${trimmed}_${category}`;
    }

    // Any other base string: append '_${category}'
    return `${trimmed}_${category}`;
  }

  return `${provider}_${category}`;
};

/**
 * Returns the currently active database (concatenated with category: e.g. theearcasino_slot).
 * Priority: localStorage override -> getComputedDatabase()
 * @returns {string}
 */
export const getActiveDatabase = () => {
  try {
    const override = localStorage.getItem(DB_STORAGE_KEY);
    if (override && typeof override === "string" && override.trim() !== "") {
      return getComputedDatabase(override.trim());
    }
  } catch (error) {
    console.error("Error reading database from localStorage:", error);
  }
  return getComputedDatabase();
};

/**
 * Alias for getActiveDatabase
 */
export const getDatabase = getActiveDatabase;

/**
 * Sets the active database in localStorage.
 * If set to 'theearcasino' or empty, the key is removed.
 * @param {string} dbName
 */
export const setActiveDatabase = (dbName) => {
  try {
    if (dbName && dbName !== DEFAULT_DATABASE) {
      localStorage.setItem(DB_STORAGE_KEY, dbName);
    } else {
      clearActiveDatabase();
    }
  } catch (error) {
    console.error("Error saving database to localStorage:", error);
  }
};

/**
 * Clears the database override from localStorage, restoring the default.
 */
export const clearActiveDatabase = () => {
  try {
    localStorage.removeItem(DB_STORAGE_KEY);
  } catch (error) {
    console.error("Error removing database from localStorage:", error);
  }
};

/**
 * Toggles live casino database filter.
 * If 'livecasino' is already active, removes the filter and returns 'theearcasino'.
 * Otherwise sets 'livecasino' and returns 'livecasino'.
 * @param {string} [game='livecasino']
 * @returns {string} The newly set database
 */
export const toggleLiveCasinoFilter = (game = "livecasino") => {
  const targetDb =
    DB_MAPPING[game.toUpperCase()] || game || DB_MAPPING.LIVECASINO;
  if (
    getActiveCategory() === targetDb ||
    getActiveDatabase().includes(targetDb)
  ) {
    clearActiveDatabase();
    return DEFAULT_DATABASE;
  } else {
    setActiveDatabase(targetDb);
    return targetDb;
  }
};

/**
 * Returns true if Live Casino is currently active.
 * @returns {boolean}
 */
export const isLiveCasinoActive = () => {
  return (
    getActiveCategory() === "livecasino" ||
    getActiveDatabase().includes("livecasino")
  );
};

/**
 * Returns true if the specified database is currently active.
 * @param {string} dbName
 * @returns {boolean}
 */
export const isDatabaseActive = (dbName) => {
  return (
    getActiveDatabase() === dbName || getActiveDatabase().includes(dbName)
  );
};
